import React, { FC, PropsWithChildren, useEffect, useRef } from 'react';

import { Box } from '@rbilabs/universal-components';
import { useWindowDimensions } from 'react-native';

import { APP_MINIMUM_HEIGHT } from 'utils/constants';

export const Wrapper: FC<PropsWithChildren> = ({ children }) => {
  const dimension = useWindowDimensions();
  const height = useRef<number>(Math.max(dimension.height, APP_MINIMUM_HEIGHT));
  const inputFocused = useRef<boolean>(false);

  /**
   * At the moment, there is not an stable API to detect keyboard visibility on web app using mobile devices.
   * Detecting if the keyboard is open on a mobile device using JavaScript can be a bit tricky
   * Keyboard visibility detection is possible by listening to focus and blur events on the document.
   * TODO: try to use the experimental VirtualKeyboard_API once it's ready to use to make this code clear and standarized
   * https://developer.mozilla.org/en-US/docs/Web/API/VirtualKeyboard_API
   */
  useEffect(() => {
    const handleFocusBlur = (event: FocusEvent) => {
      const target = event.target;
      if (target instanceof HTMLElement && target.tagName === 'INPUT') {
        inputFocused.current = event.type === 'focus';
      }
    };

    document.addEventListener('focus', handleFocusBlur, true);
    document.addEventListener('blur', handleFocusBlur, true);

    return () => {
      document.removeEventListener('focus', handleFocusBlur, true);
      document.removeEventListener('blur', handleFocusBlur, true);
    };
  }, []);

  useEffect(() => {
    // If the input is focused, do not update the height to prevent reported issues with the keyboard on mobile devices
    // https://rbictg.atlassian.net/browse/BKPE-3622
    if (inputFocused.current) {
      return;
    }
    height.current = Math.max(dimension.height, APP_MINIMUM_HEIGHT);
  }, [dimension.height]);

  return (
    <Box backgroundColor={Styles.color.background} height={height.current}>
      {children}
    </Box>
  );
};
